import React, { useRef } from "react";
import { FieldRenderProps, FieldWrapper } from "@progress/kendo-react-form";
import { Error, Hint, Label } from "@progress/kendo-react-labels";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import {
  MultiViewCalendar,
  MultiViewCalendarProps,
} from "@progress/kendo-react-dateinputs";
import { CgDanger } from "react-icons/cg";
import IconComponent from "../common/IconComponent";
import moment from "moment";

const CustomCalendar: React.FC = (props: MultiViewCalendarProps) => {
  return <MultiViewCalendar {...props} views={1} />;
};

const FormDatePicker = (fieldRenderProps: FieldRenderProps) => {
  const {
    validationMessage,
    touched,
    label,
    id,
    valid,
    disabled,
    hint,
    format = "dd/MM/yyyy",
    wrapperStyle,
    wrapperClassName = "",
    hintDirection,
    futureDate = true,
    pastDate = true,
    astrike = false,
    minDate,
    maxDate,
    defaultDate,
    ...others
  } = fieldRenderProps;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const showValidationMessage: string | false | null =
    touched && validationMessage;
  const showHint: boolean = !showValidationMessage && hint;
  const hintId: string = showHint ? `${id}_hint` : "";
  const errorId: string = showValidationMessage ? `${id}_error` : "";
  const labelId: string = label ? `${id}_label` : "";

  // Strip fractional times to prevent Kendo DatePicker infinite normalization loops
  const minLimit = pastDate ? minDate : moment().startOf("day").toDate();
  const maxLimit = futureDate ? maxDate : moment().endOf("day").toDate();

  return (
    <FieldWrapper style={wrapperStyle} className={wrapperClassName}>
      <Label
        id={labelId}
        editorId={id}
        editorValid={valid}
        editorDisabled={disabled}
        className="k-form-label"
      >
        {label} {astrike && <span style={{ color: "red" }}>*</span>}
      </Label>
      <div
        ref={wrapperRef}
        onClick={(e) => {
          const target = e.target as Node;

          // Ignore clicks that originate from React portals (like the calendar popup)
          if (!wrapperRef.current?.contains(target)) {
            return;
          }

          const btn = wrapperRef.current?.querySelector(
            ".k-input-button",
          ) as HTMLButtonElement;

          // If the user directly clicked the button or its icon, let the native event handle it
          if (btn && btn.contains(target)) {
            return;
          }

          btn?.click();
        }}
        className={"k-form-field-wrap"}
      >
        <DatePicker
          calendar={CustomCalendar}
          ariaLabelledBy={labelId}
          ariaDescribedBy={`${hintId} ${errorId}`}
          valid={valid}
          id={id}
          format={format}
          // popupSettings={{
          //     animate: false,
          //     popupClass:'year-only-popup'
          // }}
          min={minLimit}
          max={maxLimit}
          defaultValue={defaultDate}
          disabled={disabled}
          {...others}
        />
        {showHint && (
          <Hint id={hintId} direction={hintDirection}>
            {hint}
          </Hint>
        )}
        {showValidationMessage && (
          <Error className="d-flex justify-content-end" id={errorId}>
            <span
              className="d-flex align-items-center"
              style={{
                background: "#DF4957",
                color: "white",
                padding: "1px 8px 1px 5px",
                borderRadius: 10,
                gap: 5,
              }}
            >
              <IconComponent icon={CgDanger} /> {validationMessage}
            </span>
          </Error>
        )}
      </div>
    </FieldWrapper>
  );
};

export default FormDatePicker;
